/**
 * Enterprise Dashboard
 * Main container for enterprise usage tracking dashboard
 */

import React, { useState, useEffect } from 'react';
import { Calendar, Building2, ArrowLeft, RefreshCw } from 'lucide-react';
import {
  EnterpriseDashboardData,
  CustomerDetailResponse,
  TimeRange,
  TIME_RANGE_OPTIONS,
} from './types';
import { fetchEnterpriseDashboard, fetchEnterpriseCustomerDetail } from './api';
import { SummaryCards } from './components/SummaryCards';
import { CustomerList } from './components/CustomerList';
import { CustomerDetail } from './components/CustomerDetail';
import { UseCaseCostBreakdown } from '../freeTier/components/UseCaseCostBreakdown';
import { CostChart } from '../freeTier/components/CostChart';
import { UseCaseRunsChart } from '../freeTier/components/UseCaseRunsChart';
import { CostByModelChart } from '../freeTier/components/CostByModelChart';
import { CostByUser } from '../freeTier/components/CostByUser';

const BRAND_PRIMARY = '#03e8d3';

export const EnterpriseDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [dashboardData, setDashboardData] = useState<EnterpriseDashboardData | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [customerDetail, setCustomerDetail] = useState<CustomerDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await fetchEnterpriseDashboard(timeRange);
    if (apiError) {
      setError(apiError);
    } else {
      setDashboardData(data);
    }
    setLoading(false);
  };

  const fetchCustomer = async (customerId: number) => {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await fetchEnterpriseCustomerDetail(customerId, timeRange);
    if (apiError) {
      setError(apiError);
    } else {
      setCustomerDetail(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomer(selectedCustomerId);
    } else {
      fetchDashboard();
    }
  }, [timeRange, selectedCustomerId]);

  const handleCustomerSelect = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setCustomerDetail(null);
  };

  const handleBackToOverview = () => {
    setSelectedCustomerId(null);
    setCustomerDetail(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: BRAND_PRIMARY }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-medium">Error loading dashboard data</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={selectedCustomerId ? () => fetchCustomer(selectedCustomerId) : fetchDashboard}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selectedCustomerId && (
            <button
              onClick={handleBackToOverview}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-6 h-6" style={{ color: BRAND_PRIMARY }} />
              {selectedCustomerId ? 'Customer Details' : 'Enterprise Usage Dashboard'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {selectedCustomerId
                ? `Viewing usage for customer ID: ${selectedCustomerId}`
                : 'Track what enterprise customers are doing'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={selectedCustomerId ? () => fetchCustomer(selectedCustomerId) : fetchDashboard}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Refresh data"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
            <span className="text-sm font-semibold text-gray-700">Time Range</span>
          </div>
          <div className="flex gap-2">
            {TIME_RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setTimeRange(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  timeRange === option.value
                    ? 'text-white shadow-sm'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                style={timeRange === option.value ? { backgroundColor: BRAND_PRIMARY } : {}}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {selectedCustomerId && customerDetail ? (
        <CustomerDetail data={customerDetail} timeRange={timeRange} />
      ) : dashboardData ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <SummaryCards summary={dashboardData.summary} />

          {/* Row 1: Use Case Cost Breakdown | Cost Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <UseCaseCostBreakdown data={dashboardData.usecase_cost_breakdown} />
            <CostChart costData={dashboardData.cost_data} granularity={dashboardData.granularity} />
          </div>

          {/* Row 2: Use Case Runs | Cost by Model */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <UseCaseRunsChart
              data={dashboardData.usecase_run_counts}
              costData={dashboardData.usecase_cost_breakdown}
            />
            <CostByModelChart data={dashboardData.cost_data.cost_by_model} />
          </div>

          {/* Cost by User */}
          <CostByUser data={dashboardData.cost_by_user} onUserSelect={handleCustomerSelect} />

          {/* Customer List */}
          <CustomerList
            customers={dashboardData.customers}
            onCustomerSelect={handleCustomerSelect}
          />
        </div>
      ) : null}
    </div>
  );
};

export default EnterpriseDashboard;
